import axios, {AxiosRequestConfig, AxiosRequestHeaders} from 'axios';
import {BlockBlobClient} from '@azure/storage-blob';
import {ContentType} from './ContentType';
import {DownloadResponse} from '../hooks/UseDownloadFile';
import {ONE_MEGABYTE_BITS} from '../init/constants';

export class RequestHandler {
    static readonly UPLOAD_BLOCK_SIZE: number = 32 * ONE_MEGABYTE_BITS; // 32 MiB max block size
    static readonly UPLOAD_MAX_NUMBER_OF_WORKERS: number = 8; // maximum number of parallel transfer workers
    static readonly UPLOAD_MAX_FILE_SIZE_WITHOUT_SPLIT: number = 128 * ONE_MEGABYTE_BITS; // 128 MiB initial transfer size
    readonly basePath: string;
    readonly token: string | null;

    constructor(basePath: string, token: string | null) {
        this.basePath = basePath;
        this.token = token;
    }

    async get<R>(url: string): Promise<R> {
        let config = this.createDefaultConfig();
        let response = await axios.get<R>(`${this.basePath}${url}`, config);
        return response.data;
    }

    async getBlob<R>(url: string, isAbsoluteUrl = false, authorizedCall = true, contentType: ContentType = ContentType.OCTET_STREAM): Promise<R> {
        let config = this.createDefaultConfig(contentType, authorizedCall);
        let requestUrl = isAbsoluteUrl ? url : `${this.basePath}${url}`;
        config.responseType = 'blob';
        config.responseEncoding = 'binary';
        let response = await axios.get<R>(requestUrl, config);
        return response.data;
    }

    async post<R, D = unknown>(url: string, data: D, contentType: ContentType = ContentType.JSON): Promise<R> {
        let config = this.createDefaultConfig(contentType);
        let response = await axios.post<R>(`${this.basePath}${url}`, data, config);
        return response.data;
    }

    async put<R, D = unknown>(url: string, data: D, contentType: ContentType = ContentType.JSON): Promise<R> {
        let config = this.createDefaultConfig(contentType);
        let response = await axios.put<R>(`${this.basePath}${url}`, data, config);
        return response.data;
    }

    async delete<R>(url: string): Promise<R> {
        let config = this.createDefaultConfig();
        let response = await axios.delete(`${this.basePath}${url}`, config);
        return response.data;
    }

    async upload<StatusResponse>(url: string, formData: FormData, file: File, contentType: ContentType = ContentType.JSON, setProgress: (progress: number) => void): Promise<StatusResponse> {
        let config = this.createDefaultConfig(contentType);
        let response = await axios.post(`${this.basePath}${url}`, formData, config);
        let tokenResponse: DownloadResponse = response.data;
        let blockBlobClient = new BlockBlobClient(tokenResponse.token);
        let blobResponse = await blockBlobClient.uploadData(file, {
            blobHTTPHeaders: {blobContentType: ContentType.OCTET_STREAM},
            onProgress: (progress) => {
                let progressNumber = Math.ceil(progress.loadedBytes / file.size * 100);
                setProgress(progressNumber);
            },
            blockSize: RequestHandler.UPLOAD_BLOCK_SIZE,
            concurrency: RequestHandler.UPLOAD_MAX_NUMBER_OF_WORKERS,
            maxSingleShotSize: RequestHandler.UPLOAD_MAX_FILE_SIZE_WITHOUT_SPLIT
        });

        if (blobResponse._response.status === 201) {
            return {status: '201', message: 'Created'} as unknown as StatusResponse;
        }

        return {status: '400', message: 'Bad Request'} as unknown as StatusResponse;
    }

    async download(url: string, params: { [key: string]: string }, isAbsoluteUrl = false): Promise<DownloadResponse> {
        let config = this.createDefaultConfig();
        let requestUrl = isAbsoluteUrl ? url : `${this.basePath}${url}`;
        if (Object.keys(params).length !== 0) {
            let paramString = Object.keys(params).map((key: string) => `${key}=${params[key]}`).join('&')
            requestUrl += `?${paramString}`;
        }

        const response = await axios.get(requestUrl, config);

        return response.data;
    }

    private createDefaultConfig(contentType: ContentType = ContentType.JSON, authorizedCall = true): AxiosRequestConfig {
        let headers: AxiosRequestHeaders = {
            'Content-Type': contentType,
            'Access-Control-Allow-Origin': '*',
            'Access-Control-Allow-Methods': 'GET,PUT,POST,DELETE,PATCH,OPTIONS'
        };

        if (authorizedCall && this.token) {
            headers.Authorization = `Bearer ${this.token}`;
        }

        return {
            headers: headers
        };
    }
}